// Растровая графика (картинки зданий и аватары войск из /public/art, webp с прозрачным фоном).
// Если картинки нет — используется векторная (isoBuilding / generateUnitIcon).
import type { RaceKey } from '@sk/shared';
import { imageLoadListeners } from './art';
import manifest from './artManifest.json';
import refreshedUnitKeys from './unitArtRefresh.json';

interface ArtMeta {
  w: number;
  h: number;
}

const buildings = manifest.buildings as Record<string, ArtMeta>;
const units = manifest.units as Record<string, ArtMeta>;
const races = manifest.races as Record<string, ArtMeta>;
const heroes = manifest.heroes as Record<string, ArtMeta>;
const refreshedUnits = new Set<string>(refreshedUnitKeys);

/** Версия ассетов — сбрасывает кэш браузера при обновлении картинок. */
const V = 'v5';

/** Нарисованная иконка ресурса (серебро, дерево, камень, еда, железо; gems — золото). */
export function resourceArtUrl(type: string): string {
  return `/art/resources/${type}.webp?${V}`;
}

export function buildingArtUrl(type: string): string | null {
  return buildings[type] ? `/art/buildings/${type}.webp?${V}` : null;
}

export function buildingArtMeta(type: string): ArtMeta | null {
  return buildings[type] ?? null;
}

/** Один адрес для показа и предзагрузки; новый путь обходит старый кэш портретов. */
export function unitPortraitArtUrl(key: string): string {
  return refreshedUnits.has(key) ? `/art/units/refresh-v2/${key}.webp` : `/art/units/${key}.webp?${V}`;
}

/** Аватар воина по расе, классу и тиру; старые ассеты без тира остаются резервным вариантом. */
export function unitArtUrl(race: RaceKey | string | null | undefined, unitKey: string): string | null {
  if (units[unitKey]) return unitPortraitArtUrl(unitKey);
  const cls = unitKey.replace(/_\d+$/, '');
  const r = race ?? 'humans';
  const tier = Number(unitKey.match(/_(\d+)$/)?.[1] ?? 1);
  const candidates = [`${r}_${cls}_${tier}`, `${r}_${cls}`, `humans_${cls}_${tier}`, `humans_${cls}`];
  const key = candidates.find((candidate) => units[candidate]);
  return key ? unitPortraitArtUrl(key) : null;
}

/** Корабль: круглая аватарка-медальон для интерфейса. */
export function shipArtUrl(type: string): string {
  if (type === 'transport') return '/art/ships/transport.webp?transport-v2';
  if (type === 'cog') return '/art/ships/cog.webp?cog-v2';
  return `/art/ships/${type}.webp?${V}`;
}

/** Направления спрайтов корабля на карте (N — вверх экрана, по часовой стрелке). */
export const SHIP_DIRS = ['E', 'SE', 'S', 'SW', 'W', 'NW', 'N', 'NE'] as const;

/** Спрайт корабля на карте в одном из 8 ракурсов. */
export function shipSpriteUrl(type: string, dir: (typeof SHIP_DIRS)[number]): string {
  if (type === 'transport') return `/art/ships/transport_${dir}.svg?transport-v1`;
  return `/art/ships/${type}_${dir}.webp?${V}`;
}

export function raceArtUrl(race: RaceKey | string): string | null {
  return races[race] ? `/art/races/${race}.webp?${V}` : null;
}

export function heroArtUrl(race: RaceKey | string, heroClass: string): string | null {
  const key = `${race}_${heroClass}`;
  return heroes[key] ? `/art/heroes/${key}.webp?${V}` : null;
}

const cache = new Map<string, HTMLImageElement>();

/** Картинка для canvas (загружается один раз; по готовности оповещает подписчиков). */
export function artImage(url: string): HTMLImageElement {
  let img = cache.get(url);
  if (!img) {
    img = new Image();
    img.decoding = 'async';
    img.onload = () => {
      for (const l of imageLoadListeners) l();
    };
    img.src = url;
    cache.set(url, img);
  }
  return img;
}
